
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  LineChart, 
  BarChart, 
  PieChart, 
  TrendingUp, 
  Users, 
  Globe, 
  Zap, 
  Clock,
  Download,
  Share2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// Placeholder chart components
const OverviewChart = () => (
  <div className="h-[300px] w-full bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg flex items-center justify-center">
    <LineChart className="w-12 h-12 text-blue-500" />
  </div>
);

const ClientsChart = () => (
  <div className="h-[300px] w-full bg-gradient-to-r from-emerald-50 to-green-50 rounded-lg flex items-center justify-center">
    <PieChart className="w-12 h-12 text-emerald-500" />
  </div>
);

const SkillsChart = () => (
  <div className="h-[300px] w-full bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg flex items-center justify-center">
    <BarChart className="w-12 h-12 text-amber-500" />
  </div>
);

const PerformanceChart = () => (
  <div className="h-[300px] w-full bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg flex items-center justify-center">
    <TrendingUp className="w-12 h-12 text-purple-500" />
  </div>
);

const Analytics = () => {
  const [timeframe, setTimeframe] = useState("month");
  const [chartTab, setChartTab] = useState("overview");
  const { toast } = useToast();

  const handleExport = () => {
    toast({
      title: "Export Started",
      description: "Your analytics data is being exported to CSV format.",
    });
  };

  const handleShare = () => {
    toast({
      title: "Report Shared",
      description: "A link to your analytics report has been copied to clipboard.",
    });
  };

  return (
    <div className="container max-w-7xl mx-auto px-4 py-8 animate-fade-in">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">Analytics Dashboard</h1>
          <p className="text-gray-600">Analyze your performance, clients, and market trends</p>
        </div>
        
        <div className="flex flex-wrap items-center gap-3">
          <Select value={timeframe} onValueChange={setTimeframe}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Select timeframe" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
            </SelectContent>
          </Select>
          
          <Button variant="outline" size="icon" onClick={handleExport}>
            <Download className="h-4 w-4" />
          </Button>
          
          <Button variant="outline" size="icon" onClick={handleShare}>
            <Share2 className="h-4 w-4" />
          </Button>
          
          <Button onClick={() => window.print()} className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700">
            Generate Report
          </Button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-full">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Total Clients</p>
                <p className="text-2xl font-bold">24</p>
                <p className="text-xs text-green-600">+3 this {timeframe}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-full">
                <Zap className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Conversion Rate</p>
                <p className="text-2xl font-bold">68%</p>
                <p className="text-xs text-green-600">+5.2% vs last {timeframe}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-full">
                <Globe className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Market Reach</p>
                <p className="text-2xl font-bold">8 Countries</p>
                <p className="text-xs text-green-600">+2 this {timeframe}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-full">
                <Clock className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Response Time</p>
                <p className="text-2xl font-bold">1.5 Hours</p>
                <p className="text-xs text-green-600">-15% vs last {timeframe}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <Tabs value={chartTab} onValueChange={setChartTab} className="space-y-6">
        <TabsList className="grid grid-cols-4 w-full max-w-xl mx-auto">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="clients">Clients</TabsTrigger>
          <TabsTrigger value="skills">Skills</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
        </TabsList>
        
        <Card>
          <CardHeader>
            <CardTitle>
              {chartTab === "overview" && "Business Overview"}
              {chartTab === "clients" && "Client Distribution"}
              {chartTab === "skills" && "Skills Demand"}
              {chartTab === "performance" && "Performance Metrics"}
            </CardTitle>
            <CardDescription>
              {chartTab === "overview" && "A holistic view of your business metrics"}
              {chartTab === "clients" && "Analysis of your client base and revenue sources"}
              {chartTab === "skills" && "Most in-demand skills in your field"}
              {chartTab === "performance" && "Key performance indicators over time"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <TabsContent value="overview" className="mt-0">
              <OverviewChart />
            </TabsContent>
            <TabsContent value="clients" className="mt-0">
              <ClientsChart />
            </TabsContent>
            <TabsContent value="skills" className="mt-0">
              <SkillsChart />
            </TabsContent>
            <TabsContent value="performance" className="mt-0">
              <PerformanceChart />
            </TabsContent>
          </CardContent>
        </Card>
      </Tabs>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Top Performing Services</CardTitle>
            <CardDescription>Services with the highest revenue and client satisfaction</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[
                { id: 1, service: "Web Development", revenue: "$42,500", clients: 12, satisfaction: 98, growth: "+15%" },
                { id: 2, service: "UI/UX Design", revenue: "$28,750", clients: 8, satisfaction: 95, growth: "+23%" },
                { id: 3, service: "SEO Optimization", revenue: "$18,200", clients: 6, satisfaction: 92, growth: "+8%" },
                { id: 4, service: "Content Creation", revenue: "$15,400", clients: 9, satisfaction: 94, growth: "+12%" },
                { id: 5, service: "Mobile App Development", revenue: "$12,800", clients: 3, satisfaction: 97, growth: "+31%" },
              ].map((service) => (
                <div key={service.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">{service.service}</p>
                    <p className="text-sm text-gray-500">{service.clients} clients · {service.satisfaction}% satisfaction</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{service.revenue}</p>
                    <p className="text-sm text-green-600">{service.growth}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Market Insights</CardTitle>
            <CardDescription>Trends and opportunities in your industry</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-4">
              <li className="flex items-start gap-2">
                <div className="p-1 mt-0.5 bg-blue-100 rounded-full">
                  <TrendingUp className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium">AI Integration Growing</p>
                  <p className="text-sm text-gray-600">Demand for AI integration in web apps has grown by 34% this quarter.</p>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <div className="p-1 mt-0.5 bg-emerald-100 rounded-full">
                  <Globe className="h-4 w-4 text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium">International Expansion</p>
                  <p className="text-sm text-gray-600">European markets show strong potential for your service offerings.</p>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <div className="p-1 mt-0.5 bg-amber-100 rounded-full">
                  <BarChart className="h-4 w-4 text-amber-600" />
                </div>
                <div>
                  <p className="font-medium">Skill Gap Opportunity</p>
                  <p className="text-sm text-gray-600">There's a growing demand for WebAssembly expertise with few qualified providers.</p>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <div className="p-1 mt-0.5 bg-purple-100 rounded-full">
                  <Users className="h-4 w-4 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium">Client Retention</p>
                  <p className="text-sm text-gray-600">Your retention rate (89%) is well above the industry average (72%).</p>
                </div>
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Analytics;
